/**
 * Field Mapping Step
 * 
 * Map source columns to target fields with auto-detection
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  Select,
  MenuItem,
  Chip,
  LinearProgress,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  AutoFixHigh as AutoMapIcon,
  Warning as WarningIcon,
  CheckCircle as SuccessIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import axios from 'axios';

interface FieldMappingStepProps {
  sessionId: string;
  entityType: string;
  fileInfo: any;
  onMappingComplete: (mappings: Record<string, string>) => void;
  onError: (error: string) => void;
  onBack: () => void;
}

const FieldMappingStep: React.FC<FieldMappingStepProps> = ({
  sessionId,
  entityType,
  fileInfo,
  onMappingComplete,
  onError,
  onBack
}) => {
  const [loading, setLoading] = useState(true);
  const [autoMapping, setAutoMapping] = useState(false);
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [fieldInfo, setFieldInfo] = useState<any>(null);
  const [confidenceScores, setConfidenceScores] = useState<Record<string, number>>({});
  const [unmappedRequired, setUnmappedRequired] = useState<string[]>([]);
  const [unmappedOptional, setUnmappedOptional] = useState<string[]>([]);

  useEffect(() => {
    loadFieldInfo();
  }, []);

  const loadFieldInfo = async () => {
    try {
      const response = await axios.get(
        `/api/v1/import/field-info/${entityType}`
      );
      
      if (response.data.success) {
        setFieldInfo(response.data.field_info);
        // Auto-map on load
        await performAutoMapping();
      }
    } catch (error: any) {
      console.error('Failed to load field info:', error);
      onError('Failed to load field information');
    } finally {
      setLoading(false);
    }
  };

  const performAutoMapping = async () => {
    setAutoMapping(true);
    try {
      const response = await axios.post(
        `/api/v1/import/auto-map?session_id=${sessionId}&confidence_threshold=0.6`
      );

      if (response.data.success) {
        const result = response.data.mapping_result;
        setMappings(result.mappings);
        setConfidenceScores(result.confidence_scores);
        setUnmappedRequired(result.unmapped_required);
        setUnmappedOptional(result.unmapped_optional);
      }
    } catch (error: any) {
      console.error('Auto-mapping failed:', error);
      onError('Auto-mapping failed. Please map fields manually.');
    } finally {
      setAutoMapping(false);
    }
  };

  const handleMappingChange = (targetField: string, sourceColumn: string) => {
    const newMappings = { ...mappings };
    
    if (sourceColumn === '') {
      // Remove mapping
      delete newMappings[targetField];
    } else {
      // Add/update mapping
      newMappings[targetField] = sourceColumn;
    }
    
    setMappings(newMappings);
    
    // Update unmapped lists
    if (fieldInfo) {
      const mappedFields = new Set(Object.keys(newMappings));
      setUnmappedRequired(
        fieldInfo.required_fields.filter((f: string) => !mappedFields.has(f))
      );
      setUnmappedOptional(
        fieldInfo.optional_fields.filter((f: string) => !mappedFields.has(f))
      );
    }
  };

  const handleContinue = () => {
    // Validate that all required fields are mapped
    if (unmappedRequired.length > 0) {
      onError(`Please map all required fields: ${unmappedRequired.join(', ')}`);
      return;
    }
    
    onMappingComplete(mappings);
  };

  const getConfidenceColor = (score: number) => {
    if (score >= 0.9) return 'success';
    if (score >= 0.7) return 'warning';
    return 'error';
  };

  const getConfidenceLabel = (score: number) => {
    if (score >= 0.9) return 'High';
    if (score >= 0.7) return 'Medium';
    return 'Low';
  };

  const formatFieldName = (field: string) => {
    return field
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="body1" gutterBottom>
          Loading field information...
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (!fieldInfo) {
    return (
      <Alert severity="error">
        Failed to load field information. Please try again.
      </Alert>
    );
  }

  const allFields = [...fieldInfo.required_fields, ...fieldInfo.optional_fields];
  const sourceColumns = fileInfo.columns || [];
  const mappedSourceColumns = new Set(Object.values(mappings));

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Map Your Fields
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Match columns from your file to the system fields
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<AutoMapIcon />}
          onClick={performAutoMapping}
          disabled={autoMapping}
        >
          {autoMapping ? 'Auto-Mapping...' : 'Re-run Auto-Map'}
        </Button>
      </Box>

      {/* Summary */}
      <Box display="flex" gap={2} mb={3}>
        <Chip
          icon={<SuccessIcon />}
          label={`${Object.keys(mappings).length} Mapped`}
          color="success"
          variant="outlined"
        />
        <Chip
          icon={<WarningIcon />}
          label={`${unmappedRequired.length} Required Unmapped`}
          color={unmappedRequired.length > 0 ? 'error' : 'default'}
          variant="outlined"
        />
        <Chip
          icon={<InfoIcon />}
          label={`${unmappedOptional.length} Optional Unmapped`}
          color="default"
          variant="outlined"
        />
      </Box>

      {/* Unmapped Required Fields Warning */}
      {unmappedRequired.length > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <strong>Required fields not mapped:</strong> {unmappedRequired.map(formatFieldName).join(', ')}
        </Alert>
      )}

      {/* Mapping Table */}
      <TableContainer component={Paper} sx={{ mb: 3, maxHeight: 400 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell width="30%"><strong>System Field</strong></TableCell>
              <TableCell width="10%"><strong>Required</strong></TableCell>
              <TableCell width="40%"><strong>Your Column</strong></TableCell>
              <TableCell width="20%"><strong>Confidence</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allFields.map((field: string) => {
              const isRequired = fieldInfo.required_fields.includes(field);
              const isMapped = field in mappings;
              const confidence = confidenceScores[field];

              return (
                <TableRow
                  key={field}
                  sx={{
                    bgcolor: isRequired && !isMapped ? 'error.lighter' : 'inherit'
                  }}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {formatFieldName(field)}
                      {isRequired && !isMapped && (
                        <Tooltip title="Required field">
                          <WarningIcon color="error" fontSize="small" />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {isRequired ? (
                      <Chip label="Required" color="error" size="small" />
                    ) : (
                      <Chip label="Optional" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    <FormControl fullWidth size="small">
                      <Select
                        value={mappings[field] || ''}
                        onChange={(e) => handleMappingChange(field, e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="">
                          <em>Not mapped</em>
                        </MenuItem>
                        {sourceColumns.map((col: string) => (
                          <MenuItem
                            key={col}
                            value={col}
                            disabled={mappedSourceColumns.has(col) && mappings[field] !== col}
                          >
                            {col}
                            {mappedSourceColumns.has(col) && mappings[field] !== col && ' (already mapped)'}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    {confidence && (
                      <Chip
                        label={`${(confidence * 100).toFixed(0)}% - ${getConfidenceLabel(confidence)}`}
                        color={getConfidenceColor(confidence)}
                        size="small"
                      />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Help Text */}
      <Box sx={{ p: 2, bgcolor: 'info.lighter', borderRadius: 1, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          💡 Mapping Tips:
        </Typography>
        <Typography variant="body2" component="div">
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>Auto-mapping uses fuzzy matching to suggest field mappings</li>
            <li>Required fields (red badge) must be mapped to continue</li>
            <li>Each source column can only be mapped to one field</li>
            <li>Optional fields can be left unmapped</li>
            <li>High confidence (green) mappings are usually correct</li>
          </ul>
        </Typography>
      </Box>

      {/* Navigation Buttons */}
      <Box display="flex" justifyContent="space-between">
        <Button onClick={onBack}>
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleContinue}
          disabled={unmappedRequired.length > 0}
        >
          Continue to Validation
        </Button>
      </Box>
    </Box>
  );
};

export default FieldMappingStep;

